import { NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { RippleDirective } from '../ripple.directive';
import { MessageNotificationService } from '../shared/services/message-notification.service';
import { DataToolsSettingsService } from './data-tools-settings.service';
import { DuplikitOptionsComponent } from './duplikit-options/duplikit-options.component';
import { DuplikitSettings, SaveOutcome } from './duplikit-settings.model';
import { MasterIdentificationService } from './master-identification-options/master-identification.service';
import { ReportFieldOptionsComponent } from './report-field-options/report-field-options/report-field-options.component';
import { WeightTableComponent } from './weight-table/weight-table.component';

@Component({
  selector: 'app-datatools-settings',
  imports: [
    NgClass,
    WeightTableComponent,
    ReportFieldOptionsComponent,
    DuplikitOptionsComponent,
    NgIf,
    RippleDirective
  ],
  templateUrl: './data-tools-settings.component.html'
})
export class DataToolsSettingsComponent implements AfterViewInit {
  optionsNeedSaving = false;
  weightsNeedSaving = false;
  reportFieldsNeedSaving = false;
  saving: any | string;

  candidateSyncRequired = false;
  candidateMasterIDScriptRequired = false;
  candidateFinderScriptRequired = false;

  contactSyncRequired = false;
  contactMasterIDScriptRequired = false;
  contactFinderScriptRequired = false;

  companySyncRequired = false;
  companyMasterIDScriptRequired = false;
  companyFinderScriptRequired = false;

  @ViewChild(WeightTableComponent)
  weightTableComponent: WeightTableComponent;

  @ViewChild(ReportFieldOptionsComponent)
  reportFieldOptionsComponent: ReportFieldOptionsComponent;

  @ViewChild(DuplikitOptionsComponent)
  optionsComponent: DuplikitOptionsComponent;

  constructor(private dataToolsSettingsService: DataToolsSettingsService,
              private messageNotificationService: MessageNotificationService,
              private masterIdentificationService: MasterIdentificationService
  ) {}

  ngAfterViewInit() {
    this.displayFooterButtons = this.formHasValidCustomQueries;
    this.dataToolsSettingsService.syncRequired.subscribe((entity) => {
      let candidateHasChanged = false;
      let contactHasChanged = false;
      let companyHasChanged = false;

      // TODO: unify various obviously identical-in-use entity names in the different options components. perhaps a new type?
      switch (entity) {
        case 'Candidate':
        case 'candidate':
          candidateHasChanged = !this.candidateSyncRequired;
          this.candidateSyncRequired = true;
          break;
        case 'ClientContact':
        case 'clientContact':
        case 'contact':
          contactHasChanged = !this.contactSyncRequired;
          this.contactSyncRequired = true;
          break;
        case 'ClientCorporation':
        case 'clientCorporation':
        case 'company':
          companyHasChanged = !this.companySyncRequired;
          this.companySyncRequired = true;
          break;
      }

      if (candidateHasChanged) {
        this.messageNotificationService.warning(`Saving your settings now will begin a candidate sync.`);
      }
      if (contactHasChanged) {
        this.messageNotificationService.warning(`Saving your settings now will begin a clientContact sync.`);
      }
      if (companyHasChanged) {
        this.messageNotificationService.warning(`Saving your settings now will begin a clientCorporation sync.`);
      }
    });
    this.dataToolsSettingsService.masterIDScriptRequired.subscribe((entity) => {
      switch (entity) {
        case 'candidate':
          this.candidateMasterIDScriptRequired = true;
          break;
        case 'clientContact':
          this.contactMasterIDScriptRequired = true;
          break;
        case 'clientCorporation':
          this.companyMasterIDScriptRequired = true;
          break;
      }
    });
    this.dataToolsSettingsService.finderScriptRequired.subscribe((entity) => {
      switch (entity) {
        case 'candidate':
          this.candidateFinderScriptRequired = true;
          break;
        case 'contact':
          this.contactFinderScriptRequired = true;
          break;
        case 'company':
          this.companyFinderScriptRequired = true;
          break;
      }
    });
  }

  saveSettings() {
    this.saving = true;
    const settings: DuplikitSettings = new DuplikitSettings(null, null, null, null, null, null, null, null);

    settings.options = this.optionsComponent.options;
    if (this.optionsComponent.scheduledDedupeSettings) {
      settings.scheduledDedupeSettings = this.optionsComponent.scheduledDedupeSettings;
    }
    settings.strategy = this.masterIdentificationService.strategySelections;
    settings.customStrategyMapping = this.masterIdentificationService.customStrategyMapping;
    settings.selectedFields = this.reportFieldOptionsComponent.selectedFields;
    settings.weights = this.weightTableComponent.weightService.weights;
    settings.customWeightSelection = this.weightTableComponent.weightService.customWeightSelection;
    settings.saveOutcomes = this.saveOutcomes();

    this.dataToolsSettingsService.saveSettings(settings).subscribe(() => {
        this.optionsNeedSaving = false;
        this.weightsNeedSaving = false;
        this.reportFieldsNeedSaving = false;

        this.candidateSyncRequired = false;
        this.candidateMasterIDScriptRequired = false;
        this.candidateFinderScriptRequired = false;

        this.contactSyncRequired = false;
        this.contactMasterIDScriptRequired = false;
        this.contactFinderScriptRequired = false;

        this.companySyncRequired = false;
        this.companyMasterIDScriptRequired = false;
        this.companyFinderScriptRequired = false;

        this.messageNotificationService.success('Settings updated.');
      },
      (err) => {
        console.log(err);
        this.messageNotificationService.error('Error saving settings.', err.error.message);
        this.saving = false;
      },
      () => {
        this.saving = false;
      }
    );
  }

  displayFooterButtons() {
    return false;
  }

  updateOptionsNeedSaving(event: boolean) {
    this.optionsNeedSaving = event;
  }

  updateWeightsNeedSaving(event: boolean) {
    this.weightsNeedSaving = event;
  }

  updateReportFieldsNeedSaving(event: boolean) {
    this.reportFieldsNeedSaving = event;
  }

  needsSaving() {
    return (this.optionsNeedSaving || this.weightsNeedSaving || this.reportFieldsNeedSaving);
  }

  formHasValidCustomQueries() {
    return this.optionsComponent.formHasValidCustomQueries();
  }

  saveOutcomes() {
    const outcomes = {};
    outcomes['Candidate'] = SaveOutcome.NO_ACTION_REQUIRED;
    outcomes['ClientContact'] = SaveOutcome.NO_ACTION_REQUIRED;
    outcomes['ClientCorporation'] = SaveOutcome.NO_ACTION_REQUIRED;

    if (this.candidateMasterIDScriptRequired) {
      outcomes['Candidate'] = SaveOutcome.RUN_MASTER_ID_SCRIPT;
    }
    if (this.candidateFinderScriptRequired) {
      outcomes['Candidate'] = SaveOutcome.RUN_FINDER_SCRIPT;
    }
    if (this.candidateSyncRequired) {
      outcomes['Candidate'] = SaveOutcome.FULL_SYNC;
    }

    if (this.contactMasterIDScriptRequired) {
      outcomes['ClientContact'] = SaveOutcome.RUN_MASTER_ID_SCRIPT;
    }
    if (this.contactFinderScriptRequired) {
      outcomes['ClientContact'] = SaveOutcome.RUN_FINDER_SCRIPT;
    }
    if (this.contactSyncRequired) {
      outcomes['ClientContact'] = SaveOutcome.FULL_SYNC;
    }

    if (this.companyMasterIDScriptRequired) {
      outcomes['ClientCorporation'] = SaveOutcome.RUN_MASTER_ID_SCRIPT;
    }
    if (this.companyFinderScriptRequired) {
      outcomes['ClientCorporation'] = SaveOutcome.RUN_FINDER_SCRIPT;
    }
    if (this.companySyncRequired) {
      outcomes['ClientCorporation'] = SaveOutcome.FULL_SYNC;
    }

    return outcomes;
  }
}
